import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ExternalLink, Pencil, X } from 'lucide-react';
import { commentIssue, getIssue, labelIssue, listLabels, listMilestones, shareIssue, updateIssue, updateIssueComment } from '@mkcore/dev/schema';
import { Badge, Button, Card, Checkbox, ErrorState, Input, Mono, NativeSelect, PageHeader, Skeleton } from '@mkcore/ui';
import { call } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import { useActionMutation, useErrorMessage } from '@/lib/mutation';
import { useCan } from '@/lib/session';
import { readListReturn } from '@/lib/list-return';
import type { IssueListSearch } from './index';
import { connectionsQuery } from '@/features/admin/connections-panel';
import { AssigneesInput } from '@/features/dev/assignees';
import { Markdown } from '@/components/markdown';
import { MarkdownEditor } from '@/components/markdown-editor';
import { CiBadge } from '@/features/dev/ci-badge';
import { IssueTime } from '@/features/dev/issue-time';
import { IssueSuggestions } from '@/features/dev/issue-suggestions';
import { IssueAgent } from '@/features/dev/issue-agent';
import { ItemAgent } from '@/features/agents/item-status';
import { IssueDependencies } from '@/features/dev/dependencies';
import { Timeline } from '@/features/dev/timeline';

export const Route = createFileRoute('/_app/dev/issues/$id')({ component: IssueDetail });

/**
 * One mirrored issue with its body and comments (CO-0390). Changes go to Forgejo first with your linked account
 * (write-through, CO-0025) and show once Forgejo answered; without a link the page says where to link. Below the
 * comments the activity (CO-0551); in the side column the dependencies (CO-0399).
 */
function IssueDetail() {
  const { t } = useTranslation();
  const { id } = Route.useParams();
  const message = useErrorMessage();
  const canUpdate = useCan('dev:update');
  const canCreate = useCan('dev:create');
  const q = useQuery({ queryKey: ['dev', 'issue', id], queryFn: () => call(getIssue, { id }) });
  const conn = useQuery(connectionsQuery);
  const forge = conn.data?.items.find((c) => c.provider === 'forgejo');
  const linked = forge?.status === 'active';
  const editable = linked && canUpdate;
  const invalidate = [['dev', 'issue'], ['dev', 'activity']];
  const update = useActionMutation(updateIssue, { invalidate });
  const label = useActionMutation(labelIssue, { invalidate });
  const comment = useActionMutation(commentIssue, { invalidate });
  const editComment = useActionMutation(updateIssueComment, { invalidate });
  // An app field, so it needs no Forgejo link (CO-0665).
  const share = useActionMutation(shareIssue, { invalidate });
  const [editTitle, setEditTitle] = useState<string | null>(null);
  const [editBody, setEditBody] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [editing, setEditing] = useState<{ id: string; body: string } | null>(null);
  const repoId = q.data?.repositoryId;
  const labels = useQuery({ queryKey: ['dev', 'label', 'list', repoId], queryFn: () => call(listLabels, { repositoryId: repoId! }), enabled: !!repoId && editable });
  const milestones = useQuery({ queryKey: ['dev', 'milestone', 'list', repoId, 'all'], queryFn: () => call(listMilestones, { repositoryId: repoId!, state: 'all' }), enabled: !!repoId && editable });

  if (q.isError) return <ErrorState title={t('errors.generic')} description={message(q.error)} />;
  if (!q.data) return <Skeleton className="h-96" />;
  const i = q.data;
  const addable = [...new Set((labels.data?.items ?? []).map((l) => l.name))].filter((n) => !i.labels.includes(n)).sort();
  return (
    <>
      <Link to="/dev/issues" search={readListReturn<IssueListSearch>('dev.issues')?.search ?? {}} className="mb-3 inline-flex items-center gap-1 text-sm text-fg-2 hover:text-fg-1">
        <ArrowLeft className="size-4" /> {t('issues.title')}
      </Link>
      {conn.data && !linked && (canUpdate || canCreate) && (
        <Card className="mb-4 border-warning/40 p-3 text-sm" role="status">
          {forge?.status === 'relink' ? t('issues.relinkFirst') : t('issues.linkFirst')}{' '}
          <Link to="/settings" className="underline">
            {t('nav.settings')}
          </Link>
        </Card>
      )}
      {editTitle !== null ? (
        <form
          className="mb-4 flex flex-wrap items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            update.mutate({ input: { id, title: editTitle } }, { onSuccess: () => setEditTitle(null) });
          }}
        >
          <Input aria-label={t('issues.titleField')} value={editTitle} onChange={(e) => setEditTitle(e.target.value)} className="min-w-72 flex-1" autoFocus />
          <Button type="submit" loading={update.isPending} disabled={!editTitle.trim()}>
            {t('common.save')}
          </Button>
          <Button type="button" variant="ghost" onClick={() => setEditTitle(null)}>
            {t('common.cancel')}
          </Button>
        </form>
      ) : (
        <PageHeader
          eyebrow={
            <Mono className="text-xs">
              {i.repository}#{i.number}
            </Mono>
          }
          title={i.title}
          description={
            <span className="inline-flex flex-wrap items-center gap-2 text-sm">
              <Badge tone={i.state === 'open' ? 'success' : 'neutral'}>{t(`issues.state.${i.state}`)}</Badge>
              {i.author && t('issues.openedBy', { author: i.author, at: formatDateTime(i.createdAt) })}
              <a href={i.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 underline">
                {t('issues.inForge')} <ExternalLink className="size-3" />
              </a>
            </span>
          }
          actions={
            editable && (
              <div className="flex gap-2">
                <Button variant="secondary" onClick={() => setEditTitle(i.title)}>
                  <Pencil /> {t('issues.editTitle')}
                </Button>
                <Button variant="secondary" loading={update.isPending && editTitle === null} onClick={() => update.mutate({ input: { id, state: i.state === 'open' ? 'closed' : 'open' } })}>
                  {i.state === 'open' ? t('issues.close') : t('issues.reopen')}
                </Button>
              </div>
            )
          }
        />
      )}
      <div className="grid gap-4 lg:grid-cols-[1fr_16rem]">
        <div className="grid content-start gap-3">
          <Card className="p-4">
            {editBody !== null ? (
              <form
                className="grid gap-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  update.mutate({ input: { id, body: editBody } }, { onSuccess: () => setEditBody(null) });
                }}
              >
                <MarkdownEditor aria-label={t('issues.bodyField')} value={editBody} onChange={setEditBody} rows={10} base={i.url} />
                <div className="flex gap-2">
                  <Button type="submit" loading={update.isPending}>
                    {t('common.save')}
                  </Button>
                  <Button type="button" variant="ghost" onClick={() => setEditBody(null)}>
                    {t('common.cancel')}
                  </Button>
                </div>
              </form>
            ) : (
              <>
                {i.body ? <Markdown text={i.body} base={i.url} onToggleTask={editable && !update.isPending ? (body) => update.mutate({ input: { id, body } }) : undefined} /> : <p className="text-sm text-fg-3">{t('issues.noBody')}</p>}
                {editable && (
                  <Button size="sm" variant="ghost" className="mt-2" onClick={() => setEditBody(i.body)}>
                    <Pencil /> {t('issues.editBody')}
                  </Button>
                )}
              </>
            )}
          </Card>
          <h2 className="text-lg font-bold">{t('issues.commentsTitle', { count: i.comments.length })}</h2>
          {i.comments.map((c) => (
            <Card key={c.id} className="p-4">
              <div className="mb-2 flex items-center gap-2 text-xs text-fg-3">
                <span>
                  <strong className="text-fg-1">{c.author ?? '—'}</strong> · {formatDateTime(c.createdAt)}
                </span>
                {linked && canUpdate && c.author === forge.accountLogin && editing?.id !== c.id && (
                  <Button size="sm" variant="ghost" className="ml-auto" onClick={() => setEditing({ id: c.id, body: c.body })}>
                    <Pencil /> {t('issues.editComment')}
                  </Button>
                )}
              </div>
              {editing?.id === c.id ? (
                <form
                  className="grid gap-2"
                  onSubmit={(e) => {
                    e.preventDefault();
                    editComment.mutate({ input: { id: c.id, body: editing.body } }, { onSuccess: () => setEditing(null) });
                  }}
                >
                  <MarkdownEditor aria-label={t('issues.commentField')} value={editing.body} onChange={(body) => setEditing({ id: c.id, body })} rows={4} base={i.url} />
                  <div className="flex gap-2">
                    <Button type="submit" size="sm" loading={editComment.isPending} disabled={!editing.body.trim()}>
                      {t('common.save')}
                    </Button>
                    <Button type="button" size="sm" variant="ghost" onClick={() => setEditing(null)}>
                      {t('common.cancel')}
                    </Button>
                  </div>
                </form>
              ) : (
                <Markdown
                  text={c.body}
                  base={i.url}
                  onToggleTask={linked && canUpdate && c.author === forge.accountLogin && !editComment.isPending ? (body) => editComment.mutate({ input: { id: c.id, body } }) : undefined}
                />
              )}
            </Card>
          ))}
          {linked && canCreate && (
            <form
              className="grid gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                comment.mutate({ input: { id, body: draft } }, { onSuccess: () => setDraft('') });
              }}
            >
              <MarkdownEditor aria-label={t('issues.newComment')} placeholder={t('issues.newCommentHint')} value={draft} onChange={setDraft} rows={4} base={i.url} />
              <div>
                <Button type="submit" loading={comment.isPending} disabled={!draft.trim()}>
                  {t('issues.comment')}
                </Button>
              </div>
            </form>
          )}
          <Timeline issueId={id} />
        </div>
        <aside className="grid content-start gap-4 text-sm">
          <div>
            <h3 className="mb-1 text-xs font-bold uppercase text-fg-3">{t('issues.labels')}</h3>
            <div className="flex flex-wrap gap-1">
              {i.labels.length
                ? i.labels.map((l) => (
                    <Badge key={l} tone="outline" className="gap-1">
                      {l}
                      {editable && (
                        <button type="button" aria-label={t('issues.removeLabel', { label: l })} className="hover:text-danger" onClick={() => label.mutate({ input: { id, remove: [l] } })}>
                          <X className="size-3" />
                        </button>
                      )}
                    </Badge>
                  ))
                : '—'}
            </div>
            {editable && addable.length > 0 && (
              <NativeSelect aria-label={t('issues.addLabel')} className="mt-2" value="" disabled={label.isPending} onChange={(e) => e.target.value && label.mutate({ input: { id, add: [e.target.value] } })}>
                <option value="">{t('issues.addLabel')}</option>
                {addable.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </NativeSelect>
            )}
          </div>
          <div>
            <h3 className="mb-1 text-xs font-bold uppercase text-fg-3">{t('issues.milestone')}</h3>
            {editable ? (
              <NativeSelect
                aria-label={t('issues.milestone')}
                value={i.milestone?.id ?? ''}
                disabled={update.isPending}
                onChange={(e) => update.mutate({ input: { id, milestoneId: e.target.value || null } })}
              >
                <option value="">{t('issues.noMilestone')}</option>
                {(milestones.data?.items ?? []).map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.title}
                  </option>
                ))}
              </NativeSelect>
            ) : (
              (i.milestone?.title ?? '—')
            )}
          </div>
          <div>
            <h3 className="mb-1 text-xs font-bold uppercase text-fg-3">{t('issues.assignees')}</h3>
            {editable ? (
              <AssigneesInput key={i.assignees.join(',')} value={i.assignees} busy={update.isPending} onSave={(assignees) => update.mutate({ input: { id, assignees } })} />
            ) : (
              i.assignees.join(', ') || '—'
            )}
          </div>
          <IssueSuggestions issue={i} editable={editable} />
          <ItemAgent type="issue" id={i.id} />
          <IssueAgent issue={{ id: i.id, ref: `${i.repository}#${i.number}`, agentId: i.agentId }} />
          <IssueDependencies issue={i} editable={editable} />
          <IssueTime key={i.id} issue={i} canEstimate={canUpdate} />
          <label className="flex items-start gap-2 text-sm">
            <Checkbox className="mt-0.5" checked={i.clientVisible} disabled={!canUpdate || share.isPending} onCheckedChange={(v) => share.mutate({ input: { id, clientVisible: v === true } })} />
            <span>
              {t('issues.clientVisible')}
              <span className="block text-xs text-fg-3">{t('issues.clientVisibleHint')}</span>
            </span>
          </label>
          <div>
            <h3 className="mb-1 text-xs font-bold uppercase text-fg-3">{t('links.title')}</h3>
            {i.links.length ? (
              <ul className="grid gap-1.5">
                {i.links.map((l) => (
                  <li key={`${l.type}-${l.id}`} className="grid gap-0.5">
                    <span className="flex flex-wrap items-center gap-1">
                      {l.type === 'pull' ? (
                        <Link to="/dev/pulls/$id" params={{ id: l.id }} className="font-bold hover:underline">
                          {l.ref}
                        </Link>
                      ) : (
                        <a href={l.url} target="_blank" rel="noreferrer" className="font-mono text-xs hover:underline">
                          {l.ref}
                        </a>
                      )}
                      <span className="text-xs text-fg-3">{t(`links.${l.kind}`)}</span>
                      {l.state && <Badge tone={l.state === 'merged' ? 'info' : l.state === 'open' ? 'success' : 'neutral'}>{t(`pulls.state.${l.state}`)}</Badge>}
                      <CiBadge state={l.ciState} />
                    </span>
                    <span className="truncate text-xs text-fg-2">{l.title}</span>
                  </li>
                ))}
              </ul>
            ) : (
              '—'
            )}
          </div>
          <p className="text-xs text-fg-3">{t('issues.synced', { at: formatDateTime(i.syncedAt) })}</p>
        </aside>
      </div>
    </>
  );
}
